// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Turning registry facts into the one line shown on each row.
 *
 * THE THRESHOLD PROBLEM IS THE PRODUCT. `ms`, `qs` and `ws` are three real,
 * enormously popular npm packages, each one edit from the others. A flat
 * "within 1 edit of a popular package" rule marks all three, plus `fs-extra`
 * against `fs-extra`-alikes, plus half the short-named ecosystem — and a tool
 * that cries wolf in its first ten seconds is uninstalled in its eleventh. Two
 * things fix it, and both are exposed as sliders with the live match list
 * underneath, because no default is right for everyone:
 *
 *   1. THE EDIT BUDGET SCALES WITH NAME LENGTH. One edit in `ws` is half the
 *      name; one edit in `python-dateutil` is nothing. The budget is one edit
 *      per `lengthPerEdit` characters, so short names get a budget of zero and
 *      stop generating noise entirely.
 *
 *   2. THE NEIGHBOUR MUST BE MUCH MORE POPULAR. A squat imitates something
 *      bigger than itself. If your package is comparably popular to its
 *      neighbour, they are two real packages with similar names, which is
 *      ordinary. This test is skipped when the package does not exist at all,
 *      because then the neighbour list is a "did you mean", not an accusation.
 *
 * There is deliberately no `malicious` verdict and no `safe` verdict. Every
 * string this module produces is a statement about what the registry reports.
 */

import { normaliseNpm, normalisePypi } from './distance.ts';
import type { Candidate, Finding, Neighbour, RegistryFacts, Verdict } from './types.ts';

export interface Thresholds {
  /** Characters of name length required per permitted edit. Higher = stricter. */
  readonly lengthPerEdit: number;
  /** How many times more popular a neighbour must be before it is mentioned. */
  readonly popularityRatio: number;
  /** A package first published within this many days is called out as new. */
  readonly newDays: number;
  /** npm weekly downloads below this is called out as little-used. */
  readonly unpopularWeekly: number;
}

export const DEFAULT_THRESHOLDS: Thresholds = {
  lengthPerEdit: 6,
  popularityRatio: 100,
  newDays: 90,
  unpopularWeekly: 1000,
};

/** Half-units, matching distance.ts: 2 per ordinary edit. Capped at three edits. */
export function editBudget(name: string, lengthPerEdit: number): number {
  const bare = name.startsWith('@') ? name.slice(name.indexOf('/') + 1) : name;
  return Math.min(3, Math.floor(bare.length / Math.max(1, lengthPerEdit))) * 2;
}

export function daysSince(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.floor((now - then) / 86_400_000);
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/**
 * Neighbours worth showing for this candidate.
 *
 * `ownPopularity` is null for a package that does not exist, and for PyPI,
 * where download counts are unreachable from a browser. In both cases the
 * ratio test cannot run: for a missing package that is correct (there is
 * nothing to compare), and for PyPI it means the list is a "names close to
 * yours" rather than a popularity-backed signal. The interface words it that
 * way rather than implying a comparison it did not make.
 */
export function relevantNeighbours(
  candidate: Candidate,
  all: readonly Neighbour[],
  ownPopularity: number | null,
  thresholds: Thresholds,
): Neighbour[] {
  const budget = editBudget(candidate.name, thresholds.lengthPerEdit);
  if (budget === 0) return [];
  const normalise = candidate.ecosystem === 'npm' ? normaliseNpm : normalisePypi;
  const self = normalise(candidate.name);
  return all
    // Belt and braces on the name as well as the cost. The worker already
    // drops an exact match, but "your package is 1 edit from your package" is
    // the single most credibility-destroying line this tool could print, and
    // the display layer should not depend on an upstream filter to avoid it.
    .filter((n) => normalise(n.name) !== self)
    .filter((n) => n.cost > 0 && n.cost <= budget)
    .filter((n) => {
      if (ownPopularity === null || ownPopularity <= 0) return true;
      return n.popularity >= ownPopularity * thresholds.popularityRatio;
    })
    .slice(0, 5);
}

export function classify(
  candidate: Candidate,
  facts: RegistryFacts,
  neighbours: readonly Neighbour[],
  thresholds: Thresholds,
  now: number,
): { verdict: Verdict; reason: string } {
  if (facts.existence === 'error') {
    return {
      verdict: 'not-checked',
      reason: `Not checked — ${facts.error ?? 'the lookup failed'}. This is not a statement that the package is missing.`,
    };
  }
  if (facts.existence === 'unchecked') {
    return { verdict: 'not-checked', reason: 'Not looked up yet.' };
  }

  const registry = candidate.ecosystem === 'npm' ? 'npm' : 'PyPI';

  if (facts.existence === 'missing') {
    const nearest = neighbours[0];
    const suffix = nearest
      ? ` The closest name that does exist is ${nearest.name}.`
      : ' No similarly-spelled package was found in the popular-name table either.';
    return {
      verdict: 'not-in-registry',
      reason: `There is no package called ${candidate.name} on ${registry}.${suffix}`,
    };
  }

  const age = daysSince(facts.firstPublished, now);
  const parts: string[] = [];

  if (age !== null && age <= thresholds.newDays) {
    const near = neighbours[0];
    parts.push(
      `First published ${age === 0 ? 'today' : plural(age, 'day')} ago.` +
        (near ? ` Its name is ${(near.cost / 2).toFixed(near.cost % 2 ? 1 : 0)} edits from ${near.name}.` : ''),
    );
    return { verdict: 'very-new', reason: parts.join(' ') };
  }

  if (neighbours.length) {
    const near = neighbours[0];
    return {
      verdict: 'near-popular-name',
      reason: `Exists, but its name is close to ${near.name}, which is far more widely used. Check you meant this one.`,
    };
  }

  if (facts.weeklyDownloads !== null && facts.weeklyDownloads < thresholds.unpopularWeekly) {
    return {
      verdict: 'unpopular',
      reason: `Exists, with ${plural(facts.weeklyDownloads, 'download')} a week.${
        facts.repository ? '' : ' It publishes no repository link.'
      }`,
    };
  }

  const established: string[] = ['Exists'];
  if (facts.weeklyDownloads !== null) established.push(`${facts.weeklyDownloads.toLocaleString()} downloads a week`);
  if (age !== null) established.push(`first published ${Math.floor(age / 365) >= 1 ? `${Math.floor(age / 365)} years` : plural(age, 'day')} ago`);
  if (facts.releaseCount) established.push(`${plural(facts.releaseCount, 'release')}`);
  return { verdict: 'established', reason: `${established.join(', ')}.` };
}

export function buildFinding(
  candidate: Candidate,
  facts: RegistryFacts,
  allNeighbours: readonly Neighbour[],
  thresholds: Thresholds,
  now: number,
  /**
   * The candidate's own popularity from the BUNDLED table, when it is in it.
   *
   * Load-bearing for PyPI, where the registry gives a browser no download
   * count at all, so without this the ratio test is skipped for every PyPI
   * package and every popular one gets flagged against its nearest neighbour.
   * Measured before this existed: `pillow` was reported as close to `willow`.
   */
  ownFromTable: number | null = null,
): Finding {
  const own = (candidate.ecosystem === 'npm' ? facts.weeklyDownloads : null) ?? ownFromTable;
  const neighbours = relevantNeighbours(candidate, allNeighbours, facts.existence === 'missing' ? null : own, thresholds);
  const { verdict, reason } = classify(candidate, facts, neighbours, thresholds, now);
  return { candidate, facts, neighbours, verdict, reason };
}

/** The one-line summary at the top. Counts, never adjectives. */
export function headline(findings: readonly Finding[]): string {
  const total = findings.length;
  if (total === 0) return 'Nothing to check yet.';
  const missing = findings.filter((f) => f.verdict === 'not-in-registry').length;
  const fresh = findings.filter((f) => f.verdict === 'very-new').length;
  const near = findings.filter((f) => f.verdict === 'near-popular-name').length;
  const unchecked = findings.filter((f) => f.verdict === 'not-checked').length;

  const clauses: string[] = [];
  clauses.push(`${plural(total, 'package')} checked`);
  if (missing) clauses.push(`${missing} ${missing === 1 ? 'does' : 'do'} not exist`);
  if (fresh) clauses.push(`${fresh} newly published`);
  if (near) clauses.push(`${near} close to a much more popular name`);
  if (unchecked) clauses.push(`${unchecked} could not be checked`);
  if (clauses.length === 1) clauses.push('every one of them exists and is established');
  return `${clauses.join(', ')}.`;
}
